import React, { useState } from 'react';
import { useEffect } from 'react';
import { TextField, Button, MenuItem, Select, InputLabel, FormControl, Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';

import {
  makeBooking,
  deleteBooking,
  getBookingStatus,
  getBookings,
  getSavedAccounts,
  getBookingConfig
} from './api';

function Home() {
  const [booking, setBooking] = useState({
    email: '',
    password: '',
    date: null,
    time_slot: '',
    court: '',
  });

  const [queue, setQueue] = useState([]);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [bookingConfig, setBookingConfig] = useState({
    debug: false,
    retries: 0,
    interval: 0,
  });


  useEffect(() => {
    const fetchAndSyncQueue = async () => {
      const bookings = await getBookings();
      const parsed = bookings.map(b => ({
        ...b,
        date: new Date(b.month + '/' + b.day + '/2025'), // TODO: year is hardcoded -> does this make a difference?
        status: b.status?.toLowerCase() || 'pending', // fallback to 'pending' if undefined
      }));
      setQueue(parsed);
    };

    fetchAndSyncQueue();
  }, []);

  useEffect(() => {
    const fetchSavedAccounts = async () => {
      try {
        const accounts = await getSavedAccounts();
        setSavedAccounts(accounts);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };

    fetchSavedAccounts();
  }, []);

  useEffect(() => {
    const fetchBookingConfig = async () => {
      const fetchedConfig = await getBookingConfig();
      setBookingConfig(fetchedConfig);
    };

    fetchBookingConfig();
  }, []);

 const handleChange = (e) => {
    setBooking({ ...booking, [e.target.name]: e.target.value });
  };

  const handleDateChange = (newDate) => {
    setBooking({ ...booking, date: newDate });
  };

  const renderStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <AccessTimeIcon sx={{ color: 'grey', mr: 1 }} />;
      case 'success':
        return <CheckCircleIcon sx={{ color: 'green', mr: 1 }} />;
      case 'failed':
        return <CancelIcon sx={{ color: 'red', mr: 1 }} />;
      default:
        return null;
    }
  };

  const handleRemoveBooking = async (index) => {
    const bookingToDelete = queue[index]

    const payload = {
      email: bookingToDelete.email,
      password: bookingToDelete.password,
      month: bookingToDelete.date.getMonth() + 1,
      day: bookingToDelete.date.getDate(),
      time_slot: bookingToDelete.time_slot,
      court: bookingToDelete.court,
    };

    try {
      const res = await deleteBooking(payload);

      if (res.message === 'Booking successfully deleted') {
        setQueue(queue.filter((_, i) => i !== index));
      } else {
        alert('Failed to delete booking: ' + (res.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting booking.');
    }
  };

  const handleSubmitBooking = async () => {
    setBooking({
      email: '',
      password: '',
      date: null,
      time_slot: '',
      court: '',
    });

    const payload = {
      email: booking.email,
      password: booking.password,
      month: booking.date.getMonth() + 1,
      day: booking.date.getDate(),
      time_slot: booking.time_slot,
      court: booking.court,
    };

    try {
      const response = await makeBooking(payload);
      if (response.message === "Account banned!") {
        alert("You are currently banned form making bookings!")
      } else {
        const status = response?.booking?.status?.toLowerCase() || 'failed';
        setQueue([...queue, { ...booking, status }]);
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('Failed to make booking.');
    }
  };

  const handleRefreshQueue = async () => {
    // Create a copy of the queue to avoid directly mutating the state
    const updatedQueue = [...queue];

    // Iterate through each booking in the queue
    for (let i = 0; i < updatedQueue.length; i++) {
      const bookingData = updatedQueue[i];

      // Prepare the data to be sent to the API
      const bookingInfo = {
        email: bookingData.email,
        password: bookingData.password,
        month: bookingData.date.getMonth() + 1, // Adjust for zero-based months
        day: bookingData.date.getDate(),
        time_slot: bookingData.time_slot,
        court: bookingData.court,
      };

      try {
        // Call the API to get the booking status
        const response = await getBookingStatus(bookingInfo);

        // If the booking exists in the system, update the status
        if (response.booking_status) {
          updatedQueue[i] = {
            ...updatedQueue[i],
            status: response.booking_status.toLowerCase(), // Update status
          };
        }
      } catch (error) {
        console.error('Error fetching booking status:', error);
      }
    }

    // fetch saved accounts
    try {
      const accounts = await getSavedAccounts();
      setSavedAccounts(accounts);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }

    // Update the state with the new queue (with updated statuses)
    setQueue(updatedQueue);
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ padding: 4 }}>
        <Box className="header-box box">
          <h1>Automated Court Booking in Graz</h1>
          <p>Enter booking details to get started. Only beachvolleyball courts are supported.</p>
          <i><strong> This app is still in development. Test your bookings before fully relying on them! </strong></i>
          {bookingConfig.debug ? (<h3 style={{color: 'red' }}>App is running in debug mode!</h3>) : null}
        </Box>

        <Box className="flex-row" sx={{ display: 'flex', gap: 3 }}>
          <Box className="booking-box" sx={{ flex: 1, padding: 2, border: '1px solid #ddd', borderRadius: 2 }}>
            <h2>Booking Details</h2>

            <Box className="booking-box" sx={{ flex: 1, padding: 2, border: '1px solid #ddd', borderRadius: 2 }}>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel>Choose Account</InputLabel>
                <Select
                  name="choose_account"
                  value={booking.email}
                  onChange={(e) => {
                    const selectedEmail = e.target.value;
                    const account = savedAccounts.find(acc => acc.email === selectedEmail);
                    if (account) {
                      setBooking(prev => ({
                        ...prev,
                        email: account.email,
                        password: account.password,
                      }));
                    }
                  }}
                >
                  {savedAccounts.map((acc, index) => (
                    <MenuItem key={index} value={acc.email}>
                      {acc.email !== "" ? acc.email : "Empty"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <h3 style={{marginTop:0}}>or</h3>

              <TextField
                label="Email"
                name="email"
                type="email"
                value={booking.email}
                onChange={handleChange}
                fullWidth
                sx={{ marginBottom: 2 }}
              />

              <TextField
                label="Password"
                name="password"
                type="password"
                value={booking.password}
                onChange={handleChange}
                fullWidth
                sx={{ marginBottom: 2 }}
              />
              <p style={{fontSize:"small"}}><strong>INFO:</strong> After a successful booking, your account will automatically be saved in the dropdown above.</p>
            </Box>

            <Box className="booking-box" sx={{ flex: 1, padding: 2, border: '1px solid #ddd', borderRadius: 2 , marginTop: 2}}>
              <h3 style={{marginTop:0}}>Enter Details</h3>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date"
                  value={booking.date}
                  onChange={handleDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                  sx={{ marginBottom: 2, width: '100%' }}
                />
              </LocalizationProvider>

              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel>Time Slot</InputLabel>
                <Select
                  name="time_slot"
                  value={booking.time_slot}
                  onChange={handleChange}
                >
                  <MenuItem value="07:00 - 08:00">07:00 - 08:00</MenuItem>
                  <MenuItem value="08:00 - 09:00">08:00 - 09:00</MenuItem>
                  <MenuItem value="09:00 - 10:00">09:00 - 10:00</MenuItem>
                  <MenuItem value="10:00 - 11:00">10:00 - 11:00</MenuItem>
                  <MenuItem value="11:00 - 12:00">11:00 - 12:00</MenuItem>
                  <MenuItem value="12:00 - 13:00">12:00 - 13:00</MenuItem>
                  <MenuItem value="13:00 - 14:00">13:00 - 14:00</MenuItem>
                  <MenuItem value="14:00 - 15:00">14:00 - 15:00</MenuItem>
                  <MenuItem value="15:00 - 16:00">15:00 - 16:00</MenuItem>
                  <MenuItem value="16:00 - 17:00">16:00 - 17:00</MenuItem>
                  <MenuItem value="17:00 - 18:00">17:00 - 18:00</MenuItem>
                  <MenuItem value="18:00 - 19:00">18:00 - 19:00</MenuItem>
                  <MenuItem value="19:00 - 20:00">19:00 - 20:00</MenuItem>
                  <MenuItem value="20:00 - 21:00">20:00 - 21:00</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel>Select Court</InputLabel>
                <Select
                  name="court"
                  value={booking.court}
                  onChange={handleChange}
                >
                  <MenuItem value="Am Grünanger/Liebenau">Am Grünanger/Liebenau</MenuItem>
                  <MenuItem value="Eustacchio-Gründe - Platz 1">Eustacchio-Gründe - Platz 1</MenuItem>
                  <MenuItem value="Hasnerplatz">Hasnerplatz</MenuItem>
                  <MenuItem value="Andritz/St.Veit">Andritz/St.Veit</MenuItem>
                  <MenuItem value="Am Eichengrund/Andritz">Am Eichengrund/Andritz</MenuItem>
                  <MenuItem value="Concordia/Ragnitz">Concordia/Ragnitz</MenuItem>
                  <MenuItem value="Oeverseepark">Oeverseepark</MenuItem>
                  <MenuItem value="Reininghaus - Platz 1">Reininghaus - Platz 1</MenuItem>
                  <MenuItem value="Reininghaus - Platz 2">Reininghaus - Platz 2</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Button
              variant="outlined"
              color="inherit"
              onClick={handleSubmitBooking}
              fullWidth
              sx={{
                marginTop: 2,
                fontFamily: "'Courier New', Courier, monospace",
                borderColor: 'currentColor',
              }}
            >
              Add Booking
            </Button>
          </Box>

          {/* Pending Bookings Queue */}
          <Box className="queue-box" sx={{ flex: 1, padding: 2, border: '1px solid #ddd', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <h2>Bookings</h2>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleRefreshQueue}  // Define this function below
              sx={{
                height: '30px',
                width: '30px',
                padding: 0,
                fontFamily: "'Courier New', Courier, monospace",
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderColor: 'lightgray',  // Lighter border color
                '&:hover': {
                  borderColor: 'gray',  // Slightly darker border on hover
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: '20px' }} />
            </Button>
          </Box>

            {queue.length > 0 ? (
              queue.map((booking, index) => (
                <Box
                  key={index}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    padding: 2,
                    mb: 2,
                    backgroundColor: '#e0e0e0',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Courier New', Courier, monospace",
                      marginBottom: 1,
                    }}
                  >
                    {booking.email}<br />
                    {booking.date?.toLocaleDateString()} at {booking.time_slot}<br />
                    {booking.court}
                  </Typography>

                  {/* Status Section */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {renderStatusIcon(booking.status)}
                    <Typography variant="body2" sx={{ fontFamily: "'Courier New', Courier, monospace" }}>
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => handleRemoveBooking(index)}
                      sx={{
                        fontFamily: "'Courier New', Courier, monospace",
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography>No pending bookings.</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}

export default Home;
